import {
  FormControlProps,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
} from "@mui/material";
import React, { ReactNode } from "react";
import { FormControlWrapper, InputWrapperProps } from "../FormControlWrapper/FormControlWrapper";

export interface ToggleButtonOption<TValue extends string | number> {
  label: React.ReactNode;
  value: TValue;
  disabled?: boolean;
}

export type ToggleButtonGroupInputProps<TValue extends string | number> = Omit<
  ToggleButtonGroupProps,
  "onChange" | "value"
> &
  InputWrapperProps & {
    FormControlProps?: FormControlProps;
    options: ToggleButtonOption<TValue>[];
    value?: TValue;
    onChange?: (event: React.MouseEvent<HTMLElement>, value: TValue | null) => void;
    required?: boolean;
    disabled?: boolean;
    toggleButtonProps?: ToggleButtonProps;
  };

/**
 * ToggleButtonGroup input with exclusive selection (radio button behavior)
 */
export const ToggleButtonGroupInput = <TValue extends string | number>({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  options,
  value,
  onChange,
  disabled,
  toggleButtonProps,
  exclusive = true,
  ...props
}: ToggleButtonGroupInputProps<TValue>): ReactNode => {
  // Prevent deselection - keep current value selected
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: TValue | null) => {
    if (newValue !== null) {
      onChange?.(event, newValue);
    }
  };

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <ToggleButtonGroup
        {...props}
        disabled={disabled}
        exclusive={exclusive}
        fullWidth={formControlProps?.fullWidth}
        onChange={handleChange}
        value={value}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            data-cy={`toggleButton-${option.value}`}
            disabled={option.disabled ?? disabled}
            value={option.value}
            {...toggleButtonProps}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControlWrapper>
  );
};
